import { useState, useRef, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ToolPageLayout from '../components/ToolPageLayout';
import { useToolGenerate } from '../hooks/useToolGenerate';
import { useAppConfig } from '@/hooks/useAppConfig';
import { pickPhotoFromLibrary } from '@/utils/nativePhoto';

export default function StyleTransferPage() {
  const { t } = useTranslation();
  const config = useAppConfig();
  const styles = config?.styles ?? [];
  const [image, setImage] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    user,
    pointsCost,
    controls,
  } = useToolGenerate('style_transfer');

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const canGenerate = !!image && styles.some((style) => style.id === selectedStyle);

  const handleGenerate = () => {
    if (!canGenerate) return;
    const styleId = styles.find((style) => style.id === selectedStyle)?.id;
    if (!styleId) return;
    doGenerate('', image ?? undefined, undefined, { styleId });
  };

  return (
    <ToolPageLayout
      title={t('extool_styleTransfer')}
      icon="ri-brush-3-fill"
      iconBg="linear-gradient(135deg, #A855F7, #C084FC)"
      iconColor="#A855F7"
      description={t('extool_styleTransfer_desc')}
      pointsCost={pointsCost}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      canGenerate={canGenerate}
      result={result}
      error={error}
      onRegenerate={doRegenerate}
      onCancel={doCancel}
      onClearError={clearError}
      controls={controls}
    >
      {/* Upload image */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #A855F7, #C084FC)' }}
          >
            <i className="ri-image-add-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('style_transfer_uploadLabel')}</span>
          <span className="text-[10px] text-red-400 ml-auto">{t('gen_required')}</span>
        </div>

        {image ? (
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-3"
            style={{ aspectRatio: '16/9', border: '1px solid rgba(255,107,157,0.15)' }}
          >
            <img src={image} alt="upload" className="w-full h-full object-contain" />
            <button
              onClick={() => setImage(null)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => pickPhotoFromLibrary(fileInputRef, setImage)}
            className="w-full rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              aspectRatio: '16/9',
              background: 'rgba(168,85,247,0.08)',
              border: '2px dashed rgba(168,85,247,0.25)',
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(168,85,247,0.12)' }}
            >
              <i className="ri-upload-cloud-2-line text-2xl text-purple-400" />
            </div>
            <p className="text-sm text-gray-500">{t('style_transfer_uploadPlaceholder')}</p>
            <p className="text-xs text-gray-400">{t('img2img_supportFormat')}</p>
          </button>
        )}
      </div>

      {/* Style selection */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #A855F7, #C084FC)' }}
          >
            <i className="ri-palette-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('style_transfer_styleLabel')}</span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {styles.map((style) => (
            <button
              key={style.id}
              onClick={() => setSelectedStyle(style.id)}
              className={`flex flex-col items-center gap-1.5 py-2 rounded-xl cursor-pointer transition-all active:scale-95 ${
                selectedStyle === style.id ? 'bg-purple-50' : 'bg-gray-50 hover:bg-gray-100'
              }`}
              style={selectedStyle === style.id ? { border: '1px solid rgba(168,85,247,0.2)' } : {}}
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden">
                <img src={style.thumbnail_url} alt={style.name} className="w-full h-full object-cover object-top" />
              </div>
              <span className={`text-[10px] ${selectedStyle === style.id ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                {style.name}
              </span>
              {style.is_vip && (
                <span className="text-[8px] text-amber-500 bg-amber-50 px-1 rounded">
                  VIP
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </ToolPageLayout>
  );
}
